import {
  forwardRef,
  HTMLAttributes,
  ReactNode,
  RefObject,
  useRef,
  useLayoutEffect,
} from 'react';
import fitty from 'fitty';
import { Box } from '@mui/material';

export const FitText = forwardRef<
  HTMLElement,
  HTMLAttributes<HTMLDivElement> & {
    children?: ReactNode;
    minSize?: number;
    maxSize?: number;
    wrapText?: boolean;
    onWidthChange?: (data: {
      textWidth: number;
      currentFontSize: number;
    }) => void;
  }
>(function ReactFitty(
  {
    children,
    minSize = 12,
    maxSize = 512,
    wrapText = false,
    onWidthChange,
    ...rest
  },

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ref: RefObject<any> | ((instance: any) => void) | null
) {
  const internalRef = useRef<HTMLDivElement>(null);

  /**
   * Need to use the correct ref because the component ref can contain a className that dynamically
   * change the text size
   */

  useLayoutEffect(() => {
    const effectRef = (ref as RefObject<HTMLDivElement>) || internalRef;
    const fitInstance = fitty(effectRef!.current, {
      minSize: minSize,
      maxSize: maxSize,
      multiLine: wrapText,
      observeMutations: {
        subtree: true,
        childList: true,
        characterData: true,
        attributeFilter: ['class'],
      },
    });

    effectRef?.current.addEventListener('fit', function (e) {
      if (internalRef.current) {
        onWidthChange?.({
          textWidth: internalRef.current.offsetWidth,
          currentFontSize: (e as CustomEvent).detail.newValue,
        });
      }
    });

    // wait browser finish text width calc with relative properties like rem and %
    // then, fit text in the next animation frame
    // maybe that needed to be handled in fitty?
    setTimeout(() => {
      fitInstance.fit();
    }, 0);

    return () => {
      fitty(effectRef.current!).unsubscribe();
    };
  }, []);

  // fitty need an extra div to avoid parent padding issue
  // see https://github.com/rikschennink/fitty/issues/20
  return (
    <Box sx={{ width: '100%' }}>
      <Box {...rest} ref={internalRef as RefObject<HTMLDivElement>}>
        {children}
      </Box>
    </Box>
  );
});
